import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import {
  formatDateInput,
  setBodyStyleObject,
  RetourOrderStatusCreated,
  convertRetourOrderStatusToReadable,
  RetourOrderStatusReadyToCheck,
  RetourOrderStatusBeingChecked,
  RetourOrderStatusApproved,
  RetourOrderStatusProcessed,
  StockStatusInStock,
  StockStatusOutOfStock,
  StockStatusLimited,
  ProductStockTypeOnRequest,
} from '../../util/util';
import { showNotification } from '../../LanghoutBetonPortal';
import {
  StyleObject,
  RetourOrder,
  ProdEdit,
  Product,
  ProductStockView,
  DeliveryOverTime,
  RetourOrderUpdateRequest,
  RetourOrderProductUpdateRequest,
} from '../../types/types';
import { repeat } from 'lit/directives/repeat.js';

import '../../elements/langhout-button/langhout-button';
import '../langhout-beton-update-pickup-sub-order/langhout-beton-update-pickup-sub-order';
import { getQueryParamURL, getSearchParam } from '../../util/list_util';
import { SearchDropdown } from '../../elements/search-dropdown/search-dropdown';
import { minusIcon, plusIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-update-retour-order')
export class UpdateRetourOrder extends LitElement {
  private readonly styleObject: StyleObject = {
    '--wrapper-width': '800px',
  };

  static styles = [
    sharedStyles,
    css`
      .row {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
      }

      .centered {
        justify-content: center;
      }

      .inner-wrapper {
        max-width: var(--wrapper-width);
        flex-grow: 1;
      }

      .wrapper {
        padding-bottom: 400px;
        display: flex;
        flex-direction: row;
        justify-content: center;
      }

      .label {
        text-align: left;
        min-width: 180px;
        font-weight: bold;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 3 * 2);
      }

      .prod-name {
        text-align: right;
        min-width: 180px;
        flex-grow: 1;
        max-width: calc(var(--wrapper-width) / 3);
      }

      .action-row {
        margin-bottom: 20px;
      }

      select {
        padding: 5px;
        width: calc(100% - 4px);
        height: 37.19px;
        border-radius: 5px;
        border: 2px solid black;
        font-size: 16px;
      }

      @media (max-width: 779px) {
        .input-column {
          margin-bottom: var(--margin-bottom);
          width: 250px;
          max-width: 250px;
        }

        search-dropdown {
          width: 250px !important;
        }

        .fill-block,
        .fill-block-checkbox,
        .fill-block-products {
          width: 0px;
        }

        .product-label {
          width: 220px;
        }

        .remove-prod {
          right: 0;
        }
      }

      @media (max-width: 925px) {
        .remove-prod {
          right: 0px !important;
        }
        .dot {
          right: 35px !important;
        }
      }

      langhout-button {
        margin-top: 20px;
        width: 200px;
      }

      .prod-row {
        position: relative;
      }

      .remove-prod {
        position: absolute;
        top: 2px;
        right: 50px;
      }

      langhout-checkbox {
        margin-left: 15px;
      }

      .label-column-prod-amount {
        width: var(--amount-column-width);
        text-align: left;
        margin-bottom: var(--margin-bottom);
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .dot {
        position: absolute;
        right: 100px;
        border: 1px solid white;
        background-color: white;
        height: 25px;
        width: 25px;
        min-width: 25px;
        border-radius: 50%;
        display: inline-block;
        margin-right: 10px;
        margin-left: 10px;
        margin-top: 5.5px;
      }

      .dot-red {
        background-color: red;
      }

      .dot-orange {
        background-color: orange;
      }

      .dot-green {
        background-color: var(--primary-color);
      }

      .weight-display {
        width: calc(var(--row-width) - var(--label-width));
        text-align: right;
      }

      .input-column-prod-amount {
        margin-bottom: 25px;
        width: 80px;
        max-width: 80px;
      }

      .amount-in-units {
        width: 120px;
        max-width: 120px;
      }

      .input-column-prod {
        margin-bottom: 25px;
        width: 300px;
        max-width: 300px;
      }

      .button-spacing {
        width: 30px;
      }
    `,
  ];

  @property({ type: Object, reflect: true })
  public retourOrder: RetourOrder = <RetourOrder>{};

  @query('#orderID')
  public orderID?: HTMLInputElement;

  @query('#status')
  public status?: HTMLSelectElement;

  @query('#retourDate')
  public retourDate?: HTMLInputElement;

  @query('#licensePlate')
  public licensePlate?: HTMLInputElement;

  @query('#transporter')
  public transporter?: HTMLInputElement;

  @query('#shippingID')
  public shippingID?: HTMLInputElement;

  @query('#customerName')
  public customerName?: HTMLInputElement;

  private currentID = '';
  private customer_code = '';

  @property({ type: Number, attribute: true, reflect: true })
  public totalWeight = 0;

  @property({ type: Array, attribute: true, reflect: true })
  public prods: ProdEdit[] = [];

  public prodIndex = 0;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  constructor() {
    super();
    this.currentID = getSearchParam(getQueryParamURL(), 'id');
    this.getRetourOrder();
    setBodyStyleObject(this.styleObject);
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="inner-wrapper">
          <div class="row centered">
            <h2>Retour order aanpassen</h2>
          </div>
          <div class="row action-row">
            <div class="label">Ordernummer LH</div>
            <div class="prod-name">
              <input
                value=${this.retourOrder.order_id}
                type="number"
                id="orderID"
                name="orderID"
                @input=${() => {
                  const orderNumber = this.orderID
                    ? parseInt(this.orderID.value)
                    : NaN;

                  this.retrieveOrderInfoExact(orderNumber.toString());
                }}
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">Status:</div>
            <div class="prod-name">
              <select id="status" name="status">
                <option
                  .selected=${
                    this.retourOrder.status == RetourOrderStatusCreated
                  }
                  value="${RetourOrderStatusCreated}"
                >
                  ${convertRetourOrderStatusToReadable(
                    RetourOrderStatusCreated,
                  )}
                </option>
                <option
                  .selected=${
                    this.retourOrder.status == RetourOrderStatusReadyToCheck
                  }
                  value="${RetourOrderStatusReadyToCheck}"
                >
                  ${convertRetourOrderStatusToReadable(
                    RetourOrderStatusReadyToCheck,
                  )}
                </option>
                <option
                  .selected=${
                    this.retourOrder.status == RetourOrderStatusBeingChecked
                  }
                  value="${RetourOrderStatusBeingChecked}"
                >
                  ${convertRetourOrderStatusToReadable(
                    RetourOrderStatusBeingChecked,
                  )}
                </option>
                <option
                  .selected=${
                    this.retourOrder.status == RetourOrderStatusApproved
                  }
                  value="${RetourOrderStatusApproved}"
                >
                  ${convertRetourOrderStatusToReadable(
                    RetourOrderStatusApproved,
                  )}
                </option>
                <option
                  .selected=${
                    this.retourOrder.status == RetourOrderStatusProcessed
                  }
                  value="${RetourOrderStatusProcessed}"
                >
                  ${convertRetourOrderStatusToReadable(
                    RetourOrderStatusProcessed,
                  )}
                </option>
              </select>
            </div>
          </div>

          <div class="row action-row">
            <div class="label">Retourdatum:</div>
            <div class="prod-name">
              <input
                type="date"
                id="retourDate"
                name="retourDate"
                value="${formatDateInput(this.retourOrder.retour_date)}"
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">Kenteken:</div>
            <div class="prod-name">
              <input
                value=${this.retourOrder.license_plate}
                type="text"
                id="licensePlate"
                name="licensePlate"
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">Transporteur:</div>
            <div class="prod-name">
              <input
                value=${this.retourOrder.transporter}
                type="text"
                id="transporter"
                name="transporter"
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">Vracht ID</div>
            <div class="prod-name">
              <input
                value=${this.retourOrder.shipping_id}
                type="text"
                id="shippingID"
                name="shippingID"
              />
            </div>
          </div>
          <div class="row action-row">
            <div class="label">Klantnaam</div>
            <div class="prod-name">
              <input
                value=${this.retourOrder.customer_name}
                type="text"
                id="customerName"
                name="customerName"
              />
            </div>
          </div>
          ${this.renderProducts()}
          <div class="row centered action-row">
            <langhout-button
              text="Annuleren"
              reverse
              @click="${() => {
                window.location.href =
                  '/view-retour-order?id=' + this.currentID;
              }}"
            ></langhout-button>
            <div class="button-spacing"></div>
            <langhout-button
              text="Opslaan"
              @click="${() => {
                this.updateRetourOrder();
              }}"
            >
            </langhout-button>
          </div>
        </div>
      </div>
    `;
  }

  renderProducts = () => {
    return html`
      <div class="row input-row">
        <div class="fill"></div>
        <div class="plus-icon" @click="${this.addProdInput}">${plusIcon()}</div>
      </div>
      ${repeat(
        this.prods ? this.prods : [],
        (prod: ProdEdit) => prod.index,
        (item: ProdEdit, index: number) => {
          return html`
            <div class="row input-row prod-row">
              <div class="input-column-prod-amount">
                <input
                  value="${item.amount ? item.amount : ''}"
                  placeholder="aantal"
                  type="number"
                  min="1"
                  step="1"
                  class="product-amount"
                  name="product amount"
                  @input="${() => {
                    this.checkStockStatus();
                    this.calculateTotalWeight();
                  }}"
                />
              </div>
              ${this.renderProductAmountUnits(index)}
              <div class="input-column-prod">
                <search-dropdown
                  .selected="${item.prod}"
                  class="search-dropdown"
                  @change="${(e: CustomEvent) => {
                    const event = JSON.parse(e.detail.value);
                    const temp = this.prods;
                    const curItem = temp[index];
                    if (curItem.prod) {
                      curItem.prod.id = event.id;
                      curItem.prod.description = event.description;
                      temp[index] = curItem;
                    }
                    this.prods = temp;
                    this.calculateTotalWeight();
                  }}"
                ></search-dropdown>
              </div>
              ${this.renderStockStatus(item.stock_status)}
              <div
                class="remove-prod"
                @click="${() => {
                  this.removeProdInput(index);
                  setTimeout(() => {
                    this.checkStockStatus();
                    this.calculateTotalWeight();
                  }, 10);
                }}"
              >
                ${minusIcon()}
              </div>
            </div>
          `;
        },
      )}
      <div class="row input-row">
        <div class="label product-label">Totaal gewicht</div>
        <div class="fill"></div>
        <div>${this.totalWeight.toLocaleString('nl-NL')} kg</div>
      </div>
    `;
  };

  checkStockStatus = async () => {
    if (!this.shadowRoot) {
      return;
    }

    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    productSelects.forEach((e: Element, index: number) => {
      if (!this.retourDate) {
        return;
      }

      const searchDropDown = <SearchDropdown>productSelects[index];
      const amountInputElement = <HTMLInputElement>prodAmountInputs[index];

      const amountValue = parseFloat(amountInputElement.value);
      const selected = searchDropDown.selected;

      if (
        !selected ||
        selected.id === undefined ||
        selected.id === '00000000-0000-0000-0000-000000000000'
      ) {
        return;
      }

      const retourDate = new Date(this.retourDate.value);
      if (selected && amountValue > 0) {
        this.checkStockStatusForIndex(index, retourDate, amountValue, selected);
      }
    });
  };

  checkStockStatusForIndex = async (
    index: number,
    retourDate: Date,
    amountValue: number,
    selected: Product,
  ) => {
    if (selected && amountValue > 0) {
      const productStockView = await this.getProductStock(selected.id);
      if (productStockView.stock_type == ProductStockTypeOnRequest) {
        return;
      }
      const toDeliver = amountValue / selected.pieces_per_unit;
      let stockAmountAtPickup = productStockView.stock_amount;
      if (productStockView.delivery_over_time) {
        productStockView.delivery_over_time.forEach(
          (delivery: DeliveryOverTime) => {
            const deliveryDate = new Date(delivery.date);
            if (deliveryDate <= retourDate) {
              stockAmountAtPickup -= delivery.amount_in_units;
            }
          },
        );
      }
      if (productStockView.planned_production_over_time) {
        productStockView.planned_production_over_time.forEach(
          (delivery: DeliveryOverTime) => {
            const deliveryDate = new Date(delivery.date);
            if (deliveryDate <= retourDate) {
              stockAmountAtPickup += delivery.amount;
            }
          },
        );
      }

      const valueWithNewPickup = stockAmountAtPickup - toDeliver;
      let stockStatus = StockStatusInStock;
      if (productStockView.not_in_stock_amount > valueWithNewPickup) {
        stockStatus = StockStatusOutOfStock;
      } else if (productStockView.limited_stock_amount > valueWithNewPickup) {
        stockStatus = StockStatusLimited;
      }
      const temp = this.prods;
      temp[index].stock_status = stockStatus;
      this.prods = [];
      this.prods = temp;
    }
  };

  addProdInput = () => {
    const temp = this.prods;
    this.prodIndex++;
    temp.push({
      amount: 0.0,
      index: this.prodIndex,
      stock_status: StockStatusInStock,
    });
    this.prods = [];
    this.prods = temp;
  };

  updateRetourOrder = async () => {
    this.disableButtons = true;
    const updateRequest = this.getRetourOrderUpdateRequest();
    await this.updateRetourOrderAPI(updateRequest);
    this.disableButtons = false;
  };

  getRetourOrderUpdateRequest = (): RetourOrderUpdateRequest => {
    return <RetourOrderUpdateRequest>{
      order_id: this.orderID ? parseInt(this.orderID.value) : 0,
      status: this.status ? this.status.value : '',
      shipping_id: this.shippingID ? this.shippingID.value : '',
      customer_name: this.customerName ? this.customerName.value : '',
      customer_code: this.customer_code,
      license_plate: this.licensePlate ? this.licensePlate.value : '',
      transporter: this.transporter ? this.transporter.value : '',
      retour_date: this.retourDate
        ? new Date(this.retourDate.value).toISOString()
        : '',
      products: this.getProducts(),
    };
  };

  getProducts = (): RetourOrderProductUpdateRequest[] => {
    if (!this.shadowRoot) {
      return [];
    }
    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    const result: Product[] = [];
    for (let i = 0; i < productSelects.length; i++) {
      const e = <SearchDropdown>productSelects[i];
      const searchDropDown = e;
      const amountInputElement = <HTMLInputElement>prodAmountInputs[i];
      if (!searchDropDown.selected) {
        showNotification(`Product nummer ${i + 1} is niet geselecteerd.`);
        return [];
      }
      if (
        !amountInputElement.value ||
        parseFloat(amountInputElement.value) == 0
      ) {
        showNotification(`Product nummer ${i + 1} heeft geen aantal ingevuld.`);
        return [];
      }
      const prod = searchDropDown.selected;
      prod.product_id = prod.id;
      prod.amount = parseFloat(amountInputElement.value);
      result.push(prod);
    }
    return result;
  };

  getRetourOrder = async () => {
    if (this.currentID === '') {
      return;
    }
    const result = await authenticatedAPICall(
      `/retour-orders/${this.currentID}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      const temp = [];
      for (let i = 0; i < result.body.products.length; i++) {
        const p = result.body.products[i];
        temp.push({
          amount: p.amount,
          index: i,
          prod: <Product>{
            id: p.id,
            description: p.description,
            pieces_per_unit: p.pieces_per_unit,
            unit: p.unit,
            code: p.code,
            weight: p.weight,
          },
          stock_status: StockStatusInStock,
        });
      }
      this.retrieveOrderInfoExact(result.body.order_id);
      this.prods = [];
      this.prods = temp;

      setTimeout(() => {
        this.calculateTotalWeight();
        this.retourOrder = result.body;
      }, 10);
    }
  };

  calculateTotalWeight = () => {
    setTimeout(() => {
      if (!this.shadowRoot) {
        return;
      }
      const productSelects =
        this.shadowRoot.querySelectorAll('.search-dropdown');
      const prodAmountInputs =
        this.shadowRoot.querySelectorAll('.product-amount');
      const result: ProdEdit[] = [];
      productSelects.forEach((e: Element, index: number) => {
        const searchDropDown = <SearchDropdown>e;
        const amountInputElement = <HTMLInputElement>prodAmountInputs[index];
        result.push(<ProdEdit>{
          amount: parseFloat(amountInputElement.value),
          prod: searchDropDown.selected ? searchDropDown.selected : undefined,
        });
      });
      let totalWeight = 0;
      result.forEach((p: ProdEdit) => {
        if (!p.prod || isNaN(p.amount)) {
          return;
        }
        totalWeight += p.amount * p.prod.weight;
      });
      totalWeight = Math.round(totalWeight);

      this.totalWeight = totalWeight;
    }, 5);
  };

  removeProdInput = (index: number) => {
    if (!this.shadowRoot) {
      return;
    }
    if (this.prods.length <= 1) {
      showNotification('A pickup order needs atleast 1 product.');
      return;
    }
    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    const newProds: ProdEdit[] = [];
    prodAmountInputs.forEach((e: Element, otherIndex: number) => {
      if (otherIndex !== index) {
        const inputElement = <HTMLInputElement>e;
        const searchDropDown = <SearchDropdown>productSelects[otherIndex];

        newProds.push({
          amount: parseFloat(inputElement.value),
          index: this.prods[otherIndex].index,
          prod: searchDropDown.selected,
          stock_status: StockStatusInStock,
        });
      }
    });
    this.prods = [];
    this.prods = newProds;
  };

  renderStockStatus = (stockStatus: number) => {
    if (stockStatus === StockStatusInStock) {
      return html`<div class="dot dot-green"></div>`;
    }
    if (stockStatus === StockStatusOutOfStock) {
      return html`<div class="dot dot-red"></div>`;
    }
    if (stockStatus === StockStatusLimited) {
      return html`<div class="dot dot-orange"></div>`;
    }
    return html`<div class="dot dot-green"></div>`;
  };

  renderProductAmountUnits = (index: number) => {
    if (!this.shadowRoot) {
      return html` <div class="amount-in-units"></div> `;
    }
    const prodAmountInputs =
      this.shadowRoot.querySelectorAll('.product-amount');
    if (!prodAmountInputs[index]) {
      return html` <div class="amount-in-units"></div> `;
    }

    const amountInputElement = <HTMLInputElement>prodAmountInputs[index];
    if (!amountInputElement) {
      return html` <div class="amount-in-units"></div> `;
    }

    const amountValue = parseFloat(amountInputElement.value);

    const productSelects = this.shadowRoot.querySelectorAll('.search-dropdown');
    if (!productSelects[index]) {
      return html` <div class="amount-in-units"></div> `;
    }

    const searchDropDown = <SearchDropdown>productSelects[index];
    if (!searchDropDown.selected) {
      return html` <div class="amount-in-units"></div> `;
    }
    return html`
      <div class="amount-in-units">
        ${
          Math.round(
            (amountValue / searchDropDown.selected.pieces_per_unit) * 1000,
          ) / 1000
        }
        ${searchDropDown.selected.unit}
      </div>
    `;
  };

  updateRetourOrderAPI = async (p: RetourOrderUpdateRequest) => {
    const resp = await authenticatedAPICall(
      `/retour-orders/${this.currentID}`,
      'PUT',
      new Headers(),
      JSON.stringify(p),
    );
    if (isSuccessfulAPIResponse(resp) && resp.body.success) {
      showNotification('Aanpassing succesvol opgeslagen');
      window.location.href = '/view-retour-order?id=' + this.currentID;
    }
  };

  getProductStock = async (id: string): Promise<ProductStockView> => {
    const result = await authenticatedAPICall(
      `/stock/products/${id}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      return result.body;
    }
    return <ProductStockView>{};
  };

  retrieveOrderInfoExact = async (orderNumber: string) => {
    if (orderNumber.toString().length < 6) {
      return;
    }
    this.searchOrderHeaderExact(orderNumber.toString());
  };

  searchOrderHeaderExact = async (orderNumber: string) => {
    const path = `/exact-globe/sales-order-header/${orderNumber}`;
    const result = await authenticatedAPICall(path, 'GET', new Headers());

    if (isSuccessfulAPIResponse(result)) {
      if (this.customerName) {
        this.customerName.value = result.body.order_debtor_name;
        this.customerName.disabled = true;
      }

      this.customer_code = result.body.order_debtor_code;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-update-retour-order': UpdateRetourOrder;
  }
}
